import { useState } from "react";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "react-query";
import styled from "styled-components";
import { Button } from "../Button";
import AddIcon from "../../assets/img/icons/add.svg";
import { StyledField } from "../../styles/StyledField";
import { addUnitMembers, changeUnitMemberRole, getUnitMembers } from "../../api/unit/members";
import { getOrganizationUsers } from "../../api/decision/access";
import { Role } from "../../constants/role";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useCurrentUser from "../../hooks/useCurrentUser";

const StyledAddAdmin = styled.div`
	display: flex;
	gap: 1.6rem;
	align-items: center;
	margin-top: 1rem;

	select {
		flex: 1;
		height: 4rem;
		padding: 0 1rem;
	}
`;

const fullName = (user) => `${user.firstName} ${user.lastName}`;

/**
 * The unit's admins (heads of the unit). Organization admins and the unit's own admins can
 * appoint another admin; removing an admin is done in the member list, where the API keeps
 * at least one.
 */
export default function UnitAdmins({ id }) {
	const { axiosPrivate } = useAxiosPrivate();
	const queryClient = useQueryClient();
	const { can } = useCurrentUser();
	const canAppoint = can("manageUnit", id);
	const [selected, setSelected] = useState("");
	const [busy, setBusy] = useState(false);

	const { data: members } = useQuery(["membersTab", id, null], () => getUnitMembers(axiosPrivate, id, null));
	// Active users of the organization (not an admin-only list, so unit admins can use it too)
	const { data: users } = useQuery("organization-users", () => getOrganizationUsers(axiosPrivate), { enabled: canAppoint });

	const memberList = Array.isArray(members) ? members : [];
	const admins = memberList.filter((member) => member.roles.includes(Role.admin)).map((member) => member.user);
	const adminIds = admins.map((user) => user.id);
	const candidates = (Array.isArray(users) ? users : []).filter((user) => !adminIds.includes(user.id));

	const appoint = async () => {
		const userId = Number(selected);
		const refresh = () => queryClient.invalidateQueries("membersTab");
		setBusy(true);
		try {
			if (memberList.some((member) => member.user.id === userId)) {
				await changeUnitMemberRole(axiosPrivate, refresh, id, userId, { roles: [Role.member, Role.admin] });
			} else {
				await addUnitMembers(axiosPrivate, refresh, id, { roles: [Role.member, Role.admin], users: [userId] });
			}
			setSelected("");
		} finally {
			setBusy(false);
		}
	};

	return (
		<StyledField mb="12px">
			<label>{admins.length > 1 ? "Unit admins" : "Unit admin"}</label>
			<span>{admins.length ? admins.map(fullName).join(", ") : "No unit admin"}</span>
			{canAppoint && (
				<StyledAddAdmin>
					<select aria-label="Choose a new unit admin" value={selected} onChange={(e) => setSelected(e.target.value)}>
						<option value="">Choose a person</option>
						{candidates.map((user) => (
							<option key={user.id} value={user.id}>
								{fullName(user)}
							</option>
						))}
					</select>
					<Button icon={AddIcon} onClick={appoint} disabled={!selected || busy}>
						Add unit admin
					</Button>
				</StyledAddAdmin>
			)}
		</StyledField>
	);
}

UnitAdmins.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
